import React from 'react';
import styled from 'styled-components';
import { useAppSelector, useAppDispatch } from '@/store';
import { DataElementContext } from '@/page-components/common/DataElementContext';
import { processComponentProps } from '@/page-components/utils/processComponentProps';
import { fetchPlayerAchievements } from '@/store/slices/playerAchievements';
import { claimAchievementReward, clearError } from '@/store/slices/achievements';

type AchievementsProps = {
  children: any;
  styleText: string;
  className: string;
  properties?: any;
};

const ModuleElementDiv = styled.div<{ $styleText: string }>((props) => props.$styleText);

const Achievements = (componentProps: AchievementsProps) => {
  let props = componentProps;

  const dispatch = useAppDispatch();
  const existingDataElementContext = React.useContext(DataElementContext);
  [props] = processComponentProps(props, existingDataElementContext);

  // Achievements and Categories
  const achievementsData = useAppSelector((state) => state.playerAchievements.data?.achievements);
  const flattenedAchievements = useAppSelector((state) => state.playerAchievements.data?.flattenedAchievements);
  const flattenedAchievementsByCategory = useAppSelector((state) => state.playerAchievements.data?.flattenedAchievementsByCategory);
  const statusFilters = useAppSelector((state) => state.playerAchievements.data?.statusFilters);
  const achievementVerticals = useAppSelector((state) => state.playerAchievements.data?.verticals);
  
  const [selectedAchievementVertical, setSelectedAchievementVertical] = React.useState<string | null>('all');
  const [selectedStatusFilter, setSelectedStatusFilter] = React.useState<string | null>(null);
  
  // Initialize selectedStatusFilter with the actual "all" filter ID when statusFilters are loaded
  React.useEffect(() => {
    if (statusFilters && statusFilters.length > 0 && !selectedStatusFilter) {
      const allFilter = statusFilters.find((filter: any) => filter.name === 'all');
      if (allFilter) {
        setSelectedStatusFilter(allFilter.id);
      }
    }
  }, [statusFilters, selectedStatusFilter]);

  // Category/Subcategory filtering state
  const [selectedCategories, setSelectedCategories] = React.useState<Set<string>>(new Set());
  const [selectedSubcategories, setSelectedSubcategories] = React.useState<Set<string>>(new Set());

  const categoriesData = React.useMemo(() => {
    if (!flattenedAchievementsByCategory) return [];
    
    return flattenedAchievementsByCategory.map((categoryGroup: any) => ({
      ...categoryGroup,
      subcategories: categoryGroup.items.map((subcategoryGroup: any) => ({
        ...subcategoryGroup
      }))
    }));
  }, [flattenedAchievementsByCategory]);

  React.useEffect(() => {
    // Initialize with all categories and subcategories selected
    if (categoriesData.length > 0) {
      const allCategories = new Set(categoriesData.map(cat => cat.categoryName));
      const allSubcategories = new Set(
        categoriesData.flatMap(cat => cat.subcategories.map((sub: { subcategoryName: any; }) => `${cat.categoryName}:${sub.subcategoryName}`))
      );
      
      setSelectedCategories(allCategories);
      setSelectedSubcategories(allSubcategories);
    }
  }, [categoriesData]);

  React.useEffect(() => {
    if (!achievementsData || Object.keys(achievementsData).length === 0) {
      dispatch(fetchPlayerAchievements());
    }
  }, [dispatch, achievementsData]);

  const onAchievementVerticalClick = (e: React.MouseEvent<HTMLElement>) => {
    const verticalId = e.currentTarget?.dataset?.verticalid?.toLowerCase();
    setSelectedAchievementVertical(verticalId ?? null); 
  };

  const onStatusFilterClick = (e: React.MouseEvent<HTMLElement>) => {
    const statusId = e.currentTarget?.dataset?.filterValue?.toLowerCase();
    console.log('Data set ');
    console.log(e.currentTarget?.dataset);
    console.log('Selected status ' + statusId);
    setSelectedStatusFilter(statusId ?? null);
  };

  const onCategoryToggle = (e: React.MouseEvent<HTMLElement>) => {
    const categoryName = e.currentTarget?.dataset?.filterField;
    if (!categoryName) return;
    
    const newSelectedCategories = new Set(selectedCategories);
    const newSelectedSubcategories = new Set(selectedSubcategories);
    
    const categoryData = categoriesData.find(cat => cat.categoryName === categoryName);
    console.log(categoryData);
    console.log(categoriesData);
    console.log(categoryName);
    if (!categoryData) return;
    
    if (selectedCategories.has(categoryName)) {
      // Uncheck category and all its subcategories
      newSelectedCategories.delete(categoryName);
      categoryData.subcategories.forEach((sub: { subcategoryName: any; }) => {
        newSelectedSubcategories.delete(`${categoryName}:${sub.subcategoryName}`);
      });
    } else {
      // Check category and all its subcategories
      newSelectedCategories.add(categoryName);
      categoryData.subcategories.forEach((sub: { subcategoryName: any; }) => {
        newSelectedSubcategories.add(`${categoryName}:${sub.subcategoryName}`);
      });
    }
    console.log(newSelectedCategories);
    setSelectedCategories(newSelectedCategories);
    setSelectedSubcategories(newSelectedSubcategories);
  };

  const onSubcategoryToggle = (e: React.MouseEvent<HTMLElement>) => {
    const subcategoryName = e.currentTarget?.dataset?.filterField;
    if (!subcategoryName) return;
    
    // Extract category name from the subcategory key format "categoryName:subcategoryName"
    const categoryName = e.currentTarget?.dataset?.categoryName;
    if (!categoryName) return;
    
    const newSelectedSubcategories = new Set(selectedSubcategories);
    const newSelectedCategories = new Set(selectedCategories);
    
    const subcategoryKey = `${categoryName}:${subcategoryName}`;
    const categoryData = categoriesData.find(cat => cat.categoryName === categoryName);
    if (!categoryData) return;
    
    if (selectedSubcategories.has(subcategoryKey)) {
      // Uncheck subcategory
      newSelectedSubcategories.delete(subcategoryKey);
      
      // Check if all subcategories of this category are now unchecked
      const categorySubcategories = categoryData.subcategories.map((sub: any) => `${categoryName}:${sub.subcategoryName}`);
      const hasAnyCheckedSub = categorySubcategories.some((sub: string) => 
        sub !== subcategoryKey && newSelectedSubcategories.has(sub)
      );
      
      if (!hasAnyCheckedSub) {
        newSelectedCategories.delete(categoryName);
      }
    } else {
      // Check subcategory
      newSelectedSubcategories.add(subcategoryKey);
      
      // Check if all subcategories of this category are now checked
      const categorySubcategories = categoryData.subcategories.map((sub: any) => `${categoryName}:${sub.subcategoryName}`);
      const allSubsChecked = categorySubcategories.every((sub: string) => 
        sub === subcategoryKey || newSelectedSubcategories.has(sub)
      );
      
      if (allSubsChecked) {
        newSelectedCategories.add(categoryName);
      }
    }
    
    setSelectedCategories(newSelectedCategories);
    setSelectedSubcategories(newSelectedSubcategories);
  };

  // Helper function to check if category is indeterminate (some but not all subcategories checked)
  const isCategoryIndeterminate = (categoryName: string) => {
    const categoryData = categoriesData.find(cat => cat.categoryName === categoryName);
    if (!categoryData) return false;
    
    const categorySubcategories = categoryData.subcategories.map((sub: { subcategoryName: any; }) => `${categoryName}:${sub.subcategoryName}`);
    const checkedSubs = categorySubcategories.filter((sub: string) => selectedSubcategories.has(sub));
    
    return checkedSubs.length > 0 && checkedSubs.length < categorySubcategories.length;
  };

  // Helper function to check achievement status
  const getAchievementStatus = (achievement: any) => {
    if (!achievement.levels || achievement.levels.length === 0) return 'locked';
    
    const currentLevel = achievement.levels.find((level: any) => level.level === achievement.currentLevel);
    if (!currentLevel) return 'locked';
    
    if (currentLevel.isCompleted && currentLevel.rewardClaimed) return 'claimed';
    if (currentLevel.isCompleted && !currentLevel.rewardClaimed) return 'unclaimed';
    if (!currentLevel.isCompleted && !currentLevel.isLocked) return 'in progress';
    return 'locked';
  };

  const filterAchievementsByStatus = (achievements: any[], statusFilterId: string) => {
    if (!statusFilterId) return achievements;
    
    const statusFilterData = statusFilters?.find((filter: any) => filter.id == statusFilterId);
    if (!statusFilterData || statusFilterData.name === 'all') return achievements;

    return achievements.filter((ach: any) => {
      const status = getAchievementStatus(ach);
      
      if (statusFilterData.name === 'completed') {
        return ach.levels?.some((level: any) => level.isCompleted);
      }
      
      return status === statusFilterData.name;
    });
  };

  const filteredAchievements = React.useMemo(() => {
    if (!achievementsData) return [];
    
    let filtered = achievementsData;
    
    // Filter by vertical
    if (selectedAchievementVertical && selectedAchievementVertical !== 'all') {
      if (selectedAchievementVertical === 'new') {
        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
        filtered = Object.values(achievementsData).filter((ach: any) => {
          if (!ach.createdAt) return false;
          const createdAtDate = new Date(ach.createdAt);
          return createdAtDate > oneMonthAgo;
        });
      } else {
        filtered = Object.values(achievementsData).filter((ach: any) => 
          ach.vertical?.toLowerCase() === selectedAchievementVertical?.toLowerCase()
        );
      }
    } else {
      filtered = Object.values(achievementsData);
    }
    
    // Filter by status
    return filterAchievementsByStatus(filtered, selectedStatusFilter || '');
  }, [achievementsData, selectedAchievementVertical, selectedStatusFilter, statusFilters]);

  const filteredAchievementsByCategory = React.useMemo(() => {
    if (!flattenedAchievementsByCategory) return [];
    
    let filtered = flattenedAchievementsByCategory;
    
    // Filter by vertical
    if (selectedAchievementVertical && selectedAchievementVertical !== 'all') {
      const filteredByVertical: any[] = [];

      if (selectedAchievementVertical === 'new') {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        
        flattenedAchievementsByCategory.forEach((categoryGroup: any) => {
          const filteredCategoryGroup = {
            ...categoryGroup,
            items: [] as any[]
          };
          
          categoryGroup.items.forEach((subcategoryGroup: any) => {
            const achievements = subcategoryGroup.items.filter((ach: any) => {
              if (!ach.createdAt) return false;
              const createdAtDate = new Date(ach.createdAt);
              return createdAtDate >= thirtyDaysAgo;
            });
            
            if (achievements.length > 0) {
              filteredCategoryGroup.items.push({
                ...subcategoryGroup,
                items: achievements
              });
            }
          });
          
          if (filteredCategoryGroup.items.length > 0) {
            filteredByVertical.push(filteredCategoryGroup);
          }
        });
      } else {
        flattenedAchievementsByCategory.forEach((categoryGroup: any) => {
          const filteredCategoryGroup = {
            ...categoryGroup,
            items: [] as any[]
          };
          
          categoryGroup.items.forEach((subcategoryGroup: any) => {
            const achievements = subcategoryGroup.items.filter((ach: any) => 
              ach.vertical?.toLowerCase() === selectedAchievementVertical?.toLowerCase()
            );
            
            if (achievements.length > 0) {
              filteredCategoryGroup.items.push({
                ...subcategoryGroup,
                items: achievements
              });
            }
          });
          
          if (filteredCategoryGroup.items.length > 0) {
            filteredByVertical.push(filteredCategoryGroup);
          }
        });
      }
      
      filtered = filteredByVertical;
    }
    
    // Filter by category/subcategory selection
    const filteredByCategory: any[] = [];
    filtered.forEach((categoryGroup: any) => {
      if (selectedCategories.has(categoryGroup.categoryName)) {
        const filteredCategoryGroup = {
          ...categoryGroup,
          items: [] as any[]
        };
        
        categoryGroup.items.forEach((subcategoryGroup: any) => {
          const subcategoryKey = `${categoryGroup.categoryName}:${subcategoryGroup.subcategoryName}`;
          if (selectedSubcategories.has(subcategoryKey)) {
            filteredCategoryGroup.items.push(subcategoryGroup);
          }
        });
        
        if (filteredCategoryGroup.items.length > 0) {
          filteredByCategory.push(filteredCategoryGroup);
        }
      }
    });
    
    filtered = filteredByCategory;
    
    // Filter by status
    if (selectedStatusFilter && selectedStatusFilter !== 'all') {
      const filteredByStatus: any[] = [];
      
      filtered.forEach((categoryGroup: any) => {
        const filteredCategoryGroup = {
          ...categoryGroup,
          items: [] as any[]
        };
        
        categoryGroup.items.forEach((subcategoryGroup: any) => {
          const achievements = filterAchievementsByStatus(subcategoryGroup.items, selectedStatusFilter);
          
          if (achievements.length > 0) {
            filteredCategoryGroup.items.push({
              ...subcategoryGroup,
              items: achievements
            });
          }
        });
        
        if (filteredCategoryGroup.items.length > 0) {
          filteredByStatus.push(filteredCategoryGroup);
        }
      });
      
      filtered = filteredByStatus;
    }
    
    return filtered;
  }, [flattenedAchievementsByCategory, selectedAchievementVertical, selectedStatusFilter, selectedCategories, selectedSubcategories]);

  const selectedPills = React.useMemo(() => {
    const pills: Array<{
      id: string;
      name: string;
      label: string;
      type: 'category' | 'subcategory';
      categoryName?: string;
    }> = [];

    // Add selected categories (only if all subcategories are selected)
    selectedCategories.forEach(categoryName => {
      if (!isCategoryIndeterminate(categoryName)) {
        const categoryData = categoriesData.find(cat => cat.categoryName === categoryName);
        pills.push({
          id: categoryName,
          name: categoryName,
          label: categoryData?.label || categoryName,
          type: 'category'
        });
      }
    });

    // Add selected subcategories (only if parent category is not fully selected)
    selectedSubcategories.forEach(subcategoryKey => {
      const [categoryName, subcategoryName] = subcategoryKey.split(':');
      if (isCategoryIndeterminate(categoryName)) {
        const categoryData = categoriesData.find(cat => cat.categoryName === categoryName);
        const subcategoryData = categoryData?.subcategories.find((sub: { subcategoryName: string; }) => sub.subcategoryName === subcategoryName);
        
        pills.push({
          id: subcategoryKey,
          name: subcategoryName,
          label: subcategoryData?.label || subcategoryName,
          type: 'subcategory',
          categoryName
        });
      }
    });

    return pills.sort((a, b) => a.label.localeCompare(b.label));
  }, [selectedCategories, selectedSubcategories, categoriesData]);

  const onRemovePill = (e: React.MouseEvent<HTMLElement>) => {
    const pillId = e.currentTarget?.dataset?.pillId;
    const pillType = e.currentTarget?.dataset?.pillType;
    if (!pillId || !pillType) return;

    if (pillType === 'category') {
      // Remove category and all its subcategories
      const newSelectedCategories = new Set(selectedCategories);
      const newSelectedSubcategories = new Set(selectedSubcategories);
      
      newSelectedCategories.delete(pillId);
      
      const categoryData = categoriesData.find(cat => cat.categoryName === pillId);
      if (categoryData) {
        categoryData.subcategories.forEach((sub: { subcategoryName: any; }) => {
          newSelectedSubcategories.delete(`${pillId}:${sub.subcategoryName}`);
        });
      }
      
      setSelectedCategories(newSelectedCategories);
      setSelectedSubcategories(newSelectedSubcategories);
    } else {
      // Remove subcategory
      const newSelectedSubcategories = new Set(selectedSubcategories);
      newSelectedSubcategories.delete(pillId);
      
      // Check if we need to update the parent category
      console.log(pillId);
      const [categoryName] = pillId.split(':');
      const categoryData = categoriesData.find(cat => cat.categoryName === categoryName);
      
      if (categoryData) {
        const categorySubcategories = categoryData.subcategories.map((sub: { subcategoryName: any; }) => `${categoryName}:${sub.subcategoryName}`);
        const hasAnyCheckedSub = categorySubcategories.some((sub: string) => 
          sub !== pillId && newSelectedSubcategories.has(sub)
        );
        
        const newSelectedCategories = new Set(selectedCategories);
        if (!hasAnyCheckedSub) {
          newSelectedCategories.delete(categoryName);
        }
        
        setSelectedCategories(newSelectedCategories);
      }
      
      setSelectedSubcategories(newSelectedSubcategories);
    }
  };

  const onRemoveAllPills = () => {
    if (categoriesData.length > 0) {
      const allCategories = new Set(categoriesData.map(cat => cat.categoryName));
      const allSubcategories = new Set(
        categoriesData.flatMap(cat => cat.subcategories.map((sub: { subcategoryName: any; }) => `${cat.categoryName}:${sub.subcategoryName}`))
      );
      
      setSelectedCategories(allCategories);
      setSelectedSubcategories(allSubcategories);
    }
  };

const [isClaimModalSuccessfullyOpened, setIsClaimModalSuccessfullyOpened] = React.useState(false);
const [claimModalAchievementId, setClaimModalAchievementId] = React.useState<string | null>(null);
const [claimModalLevel, setClaimModalLevel] = React.useState<number | null>(null);

const [isInfoModalOpened, setIsInfoModalOpened] = React.useState(false);
const [infoModalAchievementId, setInfoModalAchievementId] = React.useState<string | null>(null);

const contextValue = React.useMemo(() => {
  // Add selected property to status filters
  const statusFiltersWithSelected = statusFilters?.map((filter: any) => ({
    ...filter,
    selected: selectedStatusFilter == filter.id
  })) || [];

  const achievementVerticalsWithSelected = achievementVerticals?.map((vertical: any) => ({
    ...vertical,
    selected: selectedAchievementVertical === vertical.name || selectedAchievementVertical === vertical.id
  })) || [];

  const categoriesDataWithSelected = categoriesData.map((categoryGroup: any) => ({
    ...categoryGroup,
    selected: selectedCategories.has(categoryGroup.categoryName),
    subcategories: categoryGroup.subcategories.map((subcategoryGroup: any) => ({
      ...subcategoryGroup,
      selected: selectedSubcategories.has(`${categoryGroup.categoryName}:${subcategoryGroup.subcategoryName}`)
    }))
  }));

  return {
    ...existingDataElementContext,
    achievements: filteredAchievements,
    flattenedAchievements,
    flattenedAchievementsByCategory: filteredAchievementsByCategory,
    statusFilters: statusFiltersWithSelected,
    achievementVerticals: achievementVerticalsWithSelected,
    categoriesData: categoriesDataWithSelected,
    selectedAchievementVertical,
    selectedStatusFilter,
    selectedCategories,
    selectedSubcategories,
    selectedPills,
    onAchievementVerticalClick,
    onStatusFilterClick,
    onCategoryToggle,
    onSubcategoryToggle,
    onRemovePill,
    onRemoveAllPills,
    isCategoryIndeterminate,
    
    isClaimModalSuccessfullyOpened,
    claimModalAchievementId,
    claimModalLevel,
    onToggleClaimModal: async (achievementId: string, level: any) => {
      // console.log('Toggling claim modal:', achievementId, level);
      if (isClaimModalSuccessfullyOpened) {
        await dispatch(fetchPlayerAchievements());
        setClaimModalAchievementId(null);
        setClaimModalLevel(null);
      } else {
        setClaimModalAchievementId(achievementId || null);
        setClaimModalLevel(level || null);
      }
      setIsClaimModalSuccessfullyOpened(!isClaimModalSuccessfullyOpened);
    },
    openClaimModal: (achievementId: string, level: number) => {
      setClaimModalAchievementId(achievementId);
      setClaimModalLevel(level);
      setIsClaimModalSuccessfullyOpened(true);
    },
    closeClaimModal: async () => {
      await dispatch(fetchPlayerAchievements());
      setIsClaimModalSuccessfullyOpened(false);
      setClaimModalAchievementId(null);
      setClaimModalLevel(null);
    },
    
    isInfoModalOpened,
    infoModalAchievementId,
    onToggleInfoModal: (achievementId: string) => {
      setInfoModalAchievementId(achievementId);
      setIsInfoModalOpened(!isInfoModalOpened);
    },
    openInfoModal: (achievementId: string) => {
      setInfoModalAchievementId(achievementId);
      setIsInfoModalOpened(true);
    },
    closeInfoModal: () => {
      setIsInfoModalOpened(false);
      setInfoModalAchievementId(null);
    },
    
    getAchievementById: (achievementId: string) => {
      return filteredAchievements?.find(achievement => achievement._id === achievementId) || null;
    },
    getAchievementLevel: (achievementId: string, level: number) => {
      const achievement = filteredAchievements?.find(achievement => achievement._id === achievementId);
      return achievement?.levels?.find((lvl: any) => lvl.level === level) || null;
    },
    claimReward: async (achievementId?: string, level?: number) => {
      // Use modal state if parameters not provided
      const targetAchievementId = achievementId || claimModalAchievementId;
      const targetLevel = level || claimModalLevel;
      
      if (!targetAchievementId || !targetLevel) {
        return { success: false, error: 'Missing achievement ID or level' };
      }

      // console.log('Claiming reward for:', targetAchievementId, 'level:', targetLevel);
      try {
        const result = await dispatch(claimAchievementReward({ 
          achievementId: targetAchievementId, 
          level: targetLevel 
        })).unwrap();
        return { success: true, data: result };
      } catch (error) {
        return { success: false, error: (error as any)?.error || 'Failed to claim reward' };
      }
    },
    
    clearClaimError: () => {
      dispatch(clearError());
    }
  };
}, [
  existingDataElementContext,
  filteredAchievements,
  filteredAchievementsByCategory,
  statusFilters,
  achievementVerticals,
  categoriesData,
  selectedAchievementVertical,
  selectedStatusFilter,
  selectedCategories,
  selectedSubcategories,
  selectedPills,
  isCategoryIndeterminate,
  isClaimModalSuccessfullyOpened,
  claimModalAchievementId,
  claimModalLevel, // Add to dependencies
  isInfoModalOpened,
  infoModalAchievementId,
  dispatch,
]);

  return (
    <ModuleElementDiv className={componentProps.className ?? ''} $styleText={componentProps.styleText}>
      <DataElementContext.Provider value={contextValue}>{componentProps.children}</DataElementContext.Provider>
    </ModuleElementDiv>
  );
};

export default Achievements;
